// scheduling/components/reports/SortableTh.jsx
// Shared column sorting for report tables (Trello #309).
//
// Every report table needs the same three things — a sortable header, a visible
// indication of which column and which direction, and null-safe comparison — so they
// live here once. The arrow markup and classes are lifted verbatim from the pattern
// already used by dashboard/JobsView and OverBudgetTab, so sorting a report table looks
// and behaves exactly like sorting the My Jobs table.
//
// The first cut of these reports baked direction into each comparator, which meant a
// column could only ever sort one way and there was nothing to point an arrow at. Column
// and direction are now separate state.

import { useCallback } from 'react';
import { useUrlState } from '../../../hooks/useUrlState';

/**
 * @param {Object<string,{get:(row)=>any, dir?:'asc'|'desc'}>} specs
 *        key -> value extractor + the direction to use when the column is FIRST picked.
 *        A default per column matters: picking "Last sign-in" should start newest-first,
 *        while picking "Staff" should start A-Z.
 * @param {string} defaultCol
 * @param {string} urlKey  query-param prefix, so two tables on one page don't collide
 */
export function useSort(specs, defaultCol, urlKey = 'sort') {
  const keys = Object.keys(specs);
  const [col, setCol] = useUrlState(urlKey, defaultCol, { validate: keys });
  const [dir, setDir] = useUrlState(`${urlKey}dir`, specs[defaultCol]?.dir || 'asc', {
    validate: ['asc', 'desc'],
  });

  const toggle = useCallback((key) => {
    if (!specs[key]) return;
    if (key === col) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setCol(key); setDir(specs[key].dir || 'asc'); }
    // `specs` is a module constant in every caller, so it is intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [col, dir, setCol, setDir]);

  const sortRows = useCallback((rows) => {
    const spec = specs[col];
    if (!spec || !Array.isArray(rows)) return rows || [];
    const sign = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = spec.get(a), bv = spec.get(b);
      // Nulls last in BOTH directions. Sorting by tax % ascending should surface the
      // lowest real percentage, not the people with no hours to compute one from —
      // otherwise reversing the sort hides the rows you were looking for.
      const an = av == null || av === '', bn = bv == null || bv === '';
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
      return String(av).localeCompare(String(bv)) * sign;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [col, dir]);

  return { col, dir, toggle, sortRows };
}

/**
 * A sortable `<th>`. `title` is required by convention — nearly every report column
 * encodes a decision a two-word label can't convey.
 */
export function SortableTh({ sortKey, label, title, className = '', sort }) {
  const active = sort.col === sortKey;
  return (
    <th
      title={title}
      className={`${className} sched-mgr-th-sortable${active ? ' sched-mgr-th-sorted' : ''}`}
      onClick={() => sort.toggle(sortKey)}
    >
      {label}
      <span className={`sched-mgr-sort-arrows${active ? ' sched-mgr-sort-arrows--active' : ''}`}>
        <span className={active && sort.dir === 'asc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▲'}</span>
        <span className={active && sort.dir === 'desc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▼'}</span>
      </span>
    </th>
  );
}

/** A non-sortable `<th>`, so a header row reads consistently. */
export function PlainTh({ label, title, className = '' }) {
  return <th title={title} className={className}>{label}</th>;
}
